import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Switch, FormControlLabel,
  Snackbar, Alert, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { Add, Delete, Send } from '@mui/icons-material';
import api from '../../api/client';

interface Active911Config {
  id: number;
  agency_id: number;
  alert_email: string;
  is_active: boolean;
  send_on_create: boolean;
  send_on_dispatch: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface Agency {
  id: number;
  name: string;
}

export default function Active911Page() {
  const [configs, setConfigs] = useState<Active911Config[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ agency_id: 0, alert_email: '', send_on_create: true, send_on_dispatch: false, notes: '' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({ open: false, msg: '', severity: 'success' });

  const load = () => {
    api.get('/active911').then((r) => setConfigs(r.data));
    api.get('/agencies').then((r) => setAgencies(r.data));
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.agency_id || !form.alert_email) {
      setSnackbar({ open: true, msg: 'Agency and Alert Email are required', severity: 'error' });
      return;
    }
    try {
      await api.post('/active911', form);
      setDialogOpen(false);
      setForm({ agency_id: 0, alert_email: '', send_on_create: true, send_on_dispatch: false, notes: '' });
      load();
      setSnackbar({ open: true, msg: 'Active911 configuration added', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, msg: e?.response?.data?.detail || 'Error creating config', severity: 'error' });
    }
  };

  const handleToggle = async (config: Active911Config) => {
    await api.patch(`/active911/${config.id}`, { is_active: !config.is_active });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this Active911 configuration?')) return;
    await api.delete(`/active911/${id}`);
    load();
  };

  const handleTest = async (id: number) => {
    try {
      await api.post(`/active911/test/${id}`);
      setSnackbar({ open: true, msg: 'Test alert sent to Active911', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, msg: e?.response?.data?.detail || 'Failed to send test alert', severity: 'error' });
    }
  };

  const getAgencyName = (id: number) => agencies.find((a) => a.id === id)?.name || `Agency #${id}`;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Active911 Integration</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
          Add Configuration
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Active911 sends call alerts to responders' phones/tablets. Each agency can have its own Active911 alert email address.
          When a new call is created, the CAD will automatically email the call details to Active911 in Cadpage format.
          Get your agency's Alert Email from your Active911 account (Agency tab → Alert Email).
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Agency</TableCell>
              <TableCell>Alert Email</TableCell>
              <TableCell>On Call Create</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{getAgencyName(c.agency_id)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{c.alert_email}</TableCell>
                <TableCell>
                  <Chip label={c.send_on_create ? 'Yes' : 'No'} size="small" color={c.send_on_create ? 'success' : 'default'} />
                </TableCell>
                <TableCell>
                  <Switch checked={c.is_active} onChange={() => handleToggle(c)} size="small" />
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{c.notes}</TableCell>
                <TableCell>
                  <IconButton size="small" title="Send Test Alert" onClick={() => handleTest(c.id)}>
                    <Send fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Delete" onClick={() => handleDelete(c.id)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No Active911 configurations. Click "Add Configuration" to set up alerting for an agency.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Config Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Active911 Configuration</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Agency</InputLabel>
            <Select
              value={form.agency_id || ''}
              label="Agency"
              onChange={(e) => setForm({ ...form, agency_id: Number(e.target.value) })}
            >
              {agencies.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth label="Active911 Alert Email" sx={{ mt: 2 }}
            value={form.alert_email}
            onChange={(e) => setForm({ ...form, alert_email: e.target.value })}
            placeholder="e.g., 12345@alert.active911.com"
            helperText="Found in your Active911 account under Agency tab → Alert Email"
          />
          <FormControlLabel
            sx={{ mt: 2 }}
            control={<Switch checked={form.send_on_create} onChange={(e) => setForm({ ...form, send_on_create: e.target.checked })} />}
            label="Send alert when a new call is created"
          />
          <FormControlLabel
            control={<Switch checked={form.send_on_dispatch} onChange={(e) => setForm({ ...form, send_on_dispatch: e.target.checked })} />}
            label="Send alert when units are dispatched"
          />
          <TextField
            fullWidth label="Notes" sx={{ mt: 2 }} multiline rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional notes (e.g., Fire Department alerts)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Add</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
